/**
 * Vanilla JavaScript replacements for lodash functions
 * These utilities maintain the same API as lodash for easy drop-in replacement
 */

/**
 * Checks if value is an empty object, collection, map, or set.
 * Objects are considered empty if they have no own enumerable string keyed properties.
 * Array-like values such as arguments objects, arrays, buffers, strings, or jQuery-like collections are considered empty if they have a length of 0.
 * Similarly, maps and sets are considered empty if they have a size of 0.
 */
export const isEmpty = (value: any): boolean => {
  if (value === null || value === undefined) return true;
  if (typeof value === "boolean" || typeof value === "number") return false;
  if (typeof value === "string" || Array.isArray(value))
    return value.length === 0;
  if (value instanceof Map || value instanceof Set) return value.size === 0;
  if (typeof value === "object") return Object.keys(value).length === 0;
  return false;
};

/**
 * Performs a deep comparison between two values to determine if they are equivalent.
 */
export const isEqual = (a: any, b: any): boolean => {
  if (a === b) return true;
  if (a == null || b == null) return false;
  if (typeof a !== typeof b) return false;

  if (Array.isArray(a)) {
    if (!Array.isArray(b) || a.length !== b.length) return false;
    return a.every((item, index) => isEqual(item, b[index]));
  }

  if (a instanceof Date && b instanceof Date) {
    return a.getTime() === b.getTime();
  }

  if (typeof a === "object" && typeof b === "object") {
    const keysA = Object.keys(a);
    const keysB = Object.keys(b);
    if (keysA.length !== keysB.length) return false;
    return keysA.every((key) => isEqual(a[key], b[key]));
  }

  return false;
};

/**
 * Performs a partial deep comparison between object and source to determine if object contains equivalent property values.
 */
export const isMatch = (object: any, source: any): boolean => {
  if (source === null || source === undefined) return true;
  if (object === null || object === undefined) return false;

  if (typeof source !== "object") return isEqual(object, source);

  return Object.keys(source).every((key) => {
    if (!(key in object)) return false;
    if (typeof source[key] === "object" && source[key] !== null) {
      return isMatch(object[key], source[key]);
    }
    return isEqual(object[key], source[key]);
  });
};

/**
 * The opposite of pick; this method creates an object composed of the own and inherited enumerable property paths of object that are not omitted.
 */
export const omit = <T extends Record<string, any>>(
  object: T,
  paths: string | string[]
): Partial<T> => {
  const pathsArray = Array.isArray(paths) ? paths : [paths];
  const result: any = {};

  Object.keys(object).forEach((key) => {
    if (!pathsArray.includes(key)) {
      result[key] = object[key];
    }
  });

  return result;
};

/**
 * This method is like clone except that it recursively clones value.
 */
export const cloneDeep = <T>(obj: T): T => {
  if (obj === null || typeof obj !== "object") return obj;
  if (obj instanceof Date) return new Date(obj.getTime()) as unknown as T;
  if (Array.isArray(obj)) return obj.map(cloneDeep) as unknown as T;
  if (typeof obj === "object") {
    const cloned: any = {};
    Object.keys(obj).forEach((key) => {
      cloned[key] = cloneDeep((obj as any)[key]);
    });
    return cloned;
  }
  return obj;
};

/**
 * Converts string to start case.
 */
export const startCase = (str: string): string => {
  return str
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .replace(/[_-]/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .split(" ")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(" ");
};

/**
 * Creates an array of elements, sorted in ascending order by the results of running each element in a collection thru each iteratee.
 */
export const orderBy = <T>(
  collection: T[],
  iteratees: (string | ((item: T) => any)) | (string | ((item: T) => any))[],
  orders?: ("asc" | "desc") | ("asc" | "desc")[] | boolean | boolean[]
): T[] => {
  const iterateesArray = Array.isArray(iteratees) ? iteratees : [iteratees];
  const ordersArray = orders
    ? Array.isArray(orders)
      ? orders
      : [orders]
    : iterateesArray.map(() => "asc" as const);

  return [...collection].sort((a, b) => {
    for (let i = 0; i < iterateesArray.length; i++) {
      const iteratee = iterateesArray[i];
      if (iteratee === undefined) continue;
      const orderValue = ordersArray[i] || "asc";
      // Convert boolean to string: true = 'asc', false = 'desc'
      const order =
        typeof orderValue === "boolean"
          ? orderValue
            ? "asc"
            : "desc"
          : orderValue;

      let valueA: any;
      let valueB: any;

      if (typeof iteratee === "string") {
        valueA = (a as any)[iteratee];
        valueB = (b as any)[iteratee];
      } else {
        valueA = iteratee(a);
        valueB = iteratee(b);
      }

      if (valueA < valueB) {
        return order === "asc" ? -1 : 1;
      }
      if (valueA > valueB) {
        return order === "asc" ? 1 : -1;
      }
    }
    return 0;
  });
};

/**
 * Creates a debounced function that delays invoking func until after wait milliseconds have elapsed since the last time the debounced function was invoked.
 */
export const debounce = <T extends (...args: any[]) => any>(
  func: T,
  wait: number,
  options?: { leading?: boolean; trailing?: boolean }
): T & { cancel: () => void } => {
  let timeoutId: NodeJS.Timeout | undefined;
  let lastCallTime: number | undefined;
  let result: any;

  const { leading = false, trailing = true } = options || {};

  const debounced = function (this: any, ...args: Parameters<T>) {
    const now = Date.now();
    const isFirstCall = !lastCallTime;

    if (timeoutId) {
      clearTimeout(timeoutId);
    }

    lastCallTime = now;

    const callNow = leading && isFirstCall;

    if (callNow) {
      result = func.apply(this, args);
    }

    timeoutId = setTimeout(() => {
      if (trailing && !callNow) {
        result = func.apply(this, args);
      }
      timeoutId = undefined;
      lastCallTime = undefined;
    }, wait);

    return result;
  };

  debounced.cancel = () => {
    if (timeoutId) {
      clearTimeout(timeoutId);
      timeoutId = undefined;
      lastCallTime = undefined;
    }
  };

  return debounced as T & { cancel: () => void };
};

/**
 * Converts the first character of string to upper case and the remaining to lower case.
 */
export const upperFirst = (str: string): string => {
  if (!str) return str;
  return str.charAt(0).toUpperCase() + str.slice(1);
};

/**
 * Creates an array of elements, sorted in ascending order by the results of running each element in a collection thru each iteratee.
 */
export const sortBy = <T>(
  collection: T[],
  iteratees?: (string | ((item: T) => any)) | (string | ((item: T) => any))[]
): T[] => {
  // If no iteratees provided, sort by identity
  if (iteratees === undefined) {
    return [...collection].sort();
  }

  const iterateesArray = Array.isArray(iteratees) ? iteratees : [iteratees];
  const orders = iterateesArray.map(() => "asc" as const);
  return orderBy(collection, iterateesArray, orders);
};

/**
 * Checks if value is null.
 */
export const isNull = (value: any): value is null => {
  return value === null;
};

/**
 * Iterates over elements of collection, returning the first element predicate returns truthy for.
 */
export const find = <T>(
  collection: T[],
  predicate: ((item: T) => boolean) | string | object | [string, unknown]
): T | undefined => {
  if (typeof predicate === "function") {
    return collection.find(predicate as (item: T) => boolean);
  }

  if (Array.isArray(predicate)) {
    const [path, value] = predicate;
    return collection.find((item) => get(item, path) === value);
  }

  if (typeof predicate === "string") {
    return collection.find((item) => !!(item as any)[predicate]);
  }

  if (typeof predicate === "object") {
    return collection.find((item) => isMatch(item, predicate));
  }

  return undefined;
};

/**
 * Gets the value at path of object. If the resolved value is undefined, the defaultValue is returned in its place.
 */
export const get = (
  object: any,
  path: string | string[],
  defaultValue?: any
): any => {
  if (!object) return defaultValue;

  const pathArray = Array.isArray(path)
    ? path
    : path.replace(/\[(\d+)\]/g, ".$1").split(".");

  let result = object;

  for (const key of pathArray) {
    if (result == null || !(key in result)) {
      return defaultValue;
    }
    result = result[key];
  }

  return result === undefined ? defaultValue : result;
};

/**
 * This method is like sum except that it accepts iteratee which is invoked for each element in array to generate the value to be summed.
 */
export const sumBy = <T>(
  array: T[],
  iteratee: string | ((item: T) => number)
): number => {
  if (!Array.isArray(array)) return 0;

  return array.reduce((sum, item) => {
    const value =
      typeof iteratee === "string" ? (item as any)[iteratee] : iteratee(item);
    return sum + (Number(value) || 0);
  }, 0);
};

/**
 * Checks if value is the language type of Object. (e.g. arrays, functions, objects, regexes, new Number(0), and new String(''))
 */
export const isObject = (value: any): value is object => {
  const type = typeof value;
  return value != null && (type === "object" || type === "function");
};

/**
 * This method is like assign except that it recursively merges own and inherited enumerable string keyed properties of source objects into the destination object.
 */
export const merge = <T extends Record<string, any>>(
  target: T,
  ...sources: Partial<T>[]
): T => {
  if (!target || typeof target !== "object") return target;

  sources.forEach((source) => {
    if (!source || typeof source !== "object") return;

    Object.keys(source).forEach((key) => {
      const sourceValue = source[key];
      const targetValue = target[key];

      if (
        isObject(sourceValue) &&
        isObject(targetValue) &&
        !Array.isArray(sourceValue) &&
        !Array.isArray(targetValue)
      ) {
        (target as any)[key] = merge(targetValue, sourceValue);
      } else {
        (target as any)[key] = sourceValue;
      }
    });
  });

  return target;
};

/**
 * Sets the value at path of object. If a portion of path doesn't exist, it's created.
 */
export const set = (object: any, path: string | string[], value: any): any => {
  if (!object || typeof object !== "object") return object;

  const pathArray = Array.isArray(path)
    ? path
    : path.replace(/\[(\d+)\]/g, ".$1").split(".");

  let current = object;

  for (let i = 0; i < pathArray.length - 1; i++) {
    const key = pathArray[i];
    if (key === undefined) continue;

    if (!(key in current) || !isObject(current[key])) {
      const nextKey = pathArray[i + 1] ?? "";
      current[key] = /^\d+$/.test(nextKey) ? [] : {};
    }

    current = current[key];
  }

  const lastKey = pathArray[pathArray.length - 1];
  if (lastKey !== undefined) {
    current[lastKey] = value;
  }
  return object;
};

/**
 * Creates a function that is restricted to invoking func once.
 */
export const once = <T extends (...args: any[]) => any>(func: T): T => {
  let called = false;
  let result: any;

  return ((...args: any[]) => {
    if (!called) {
      called = true;
      result = func.apply(this, args);
    }
    return result;
  }) as T;
};

/**
 * Checks if value is undefined.
 */
export const isUndefined = (value: any): value is undefined => {
  return value === undefined;
};

/**
 * Checks if value is an integer.
 */
export const isInteger = (value: any): value is number => {
  return Number.isInteger(value);
};

/**
 * Creates an array of grouped elements, the first of which contains the first elements of the given arrays.
 */
export const groupBy = <T>(
  collection: T[],
  iteratee: string | ((item: T) => string | number)
): { [key: string]: T[] } => {
  const result: { [key: string]: T[] } = {};

  collection.forEach((item) => {
    const key =
      typeof iteratee === "string"
        ? String((item as any)[iteratee])
        : String(iteratee(item));

    if (!result[key]) {
      result[key] = [];
    }
    result[key].push(item);
  });

  return result;
};

/**
 * This method returns the first index at which a given element can be found in the array using predicate function.
 */
export const findIndex = <T>(
  array: T[],
  predicate:
    | ((item: T, index: number) => boolean)
    | string
    | object
    | [string, unknown]
): number => {
  if (typeof predicate === "function") {
    return array.findIndex(predicate as (item: T, index: number) => boolean);
  }

  if (Array.isArray(predicate)) {
    const [path, value] = predicate;
    return array.findIndex((item) => get(item, path) === value);
  }

  if (typeof predicate === "string") {
    return array.findIndex((item) => !!(item as any)[predicate]);
  }

  if (typeof predicate === "object") {
    return array.findIndex((item) => isMatch(item, predicate));
  }

  return -1;
};

/**
 * Creates an array of values by running each element in collection thru iteratee.
 */
export const differenceWith = <T>(
  array: T[],
  values: T[],
  comparator: (a: T, b: T) => boolean
): T[] => {
  return array.filter((a) => !values.some((b) => comparator(a, b)));
};

/**
 * Creates a lodash wrapper instance that wraps value with explicit method chain sequences enabled.
 */
export const chain = <T>(value: T[]) => {
  return {
    value: () => value,
    groupBy: (iteratee: string | ((item: T) => string | number)) => {
      const grouped = groupBy(value, iteratee);
      return {
        value: () => grouped,
        mapValues: (iterateeMap: (items: T[]) => any) => {
          const mapped: { [key: string]: any } = {};
          Object.entries(grouped).forEach(([key, items]) => {
            mapped[key] = iterateeMap(items);
          });
          return {
            value: () => mapped,
          };
        },
      };
    },
    map: (iteratee: string | ((item: T) => any)) => {
      const mapped = value.map((item) =>
        typeof iteratee === "string" ? (item as any)[iteratee] : iteratee(item)
      );
      return {
        value: () => mapped,
        uniq: () => {
          const unique = [...new Set(mapped)];
          return {
            value: () => unique,
          };
        },
      };
    },
  };
};
